/* eslint-disable complexity */
import { mapKeys, merge, pick } from 'lodash-es'
import { actionTypes as types } from '../constants'
import { firstDefined } from 'utils/utils'

const initialState = {
  // These three state variables control loading states in ModalPage's renderModalShell and renderBlockingSpinner.
  // isFetching is also used to determine whether data is available.
  isFetching: false,
  isFetchUIBlocking: true,
  noContent: false,
  // Display error page
  fetchError: null,
  // Display success page
  fetchSuccessAction: '',
  // Display retry request page
  fetchRetry: null,
  // Cards ID map
  cards: {},
  // Card could be fetched one at a time,
  // when user access the card details page directly,
  // Set to true when all user cards have been fetched
  isCardsFetched: false,
}

const fetchingState = (action) => ({
  isFetching: true,
  isFetchUIBlocking: firstDefined(action.isFetchUIBlocking, true),
  noContent: Boolean(action.isFetchOnMount),
  fetchError: null,
  fetchSuccessAction: '',
})

const successState = (actionType) => ({
  isFetching: false,
  isFetchUIBlocking: true, // set this to its base state even though it's meaningless when isFetching is false
  noContent: false,
  fetchRetry: null,
  fetchSuccessAction: actionType,
})

function card(state = initialState, action = {}) {
  switch (action.type) {
    case types.CREATE_CARD:
    case types.FETCH_CARDS:
    case types.FETCH_CARD:
    case types.CANCEL_CARD:
    case types.CHANGE_PIN:
    case types.CHANGE_CVV:
    case types.VIEW_PIN:
      return {
        ...state,
        ...fetchingState(action),
      }

    case types.CREATE_CARD_SUCCESS:
      return {
        ...state,
        ...successState(action.type),
        cards: merge({}, state.cards, { [action.card.cardId]: action.card }),
      }

    case types.FETCH_CARDS_SUCCESS:
      return {
        ...state,
        ...successState(action.type),
        cards: merge({}, state.cards, { ...mapKeys(action.cards, 'cardId') }),
        isCardsFetched: true,
      }

    case types.FETCH_CARD_SUCCESS:
      return {
        ...state,
        ...successState(action.type),
        cards: {
          ...state.cards,
          [action.cardId]: {
            ...state.cards[action.cardId],
            ...action.card,
          },
        },
      }

    case types.CANCEL_CARD_SUCCESS:
      return {
        ...state,
        ...successState(action.type),
        cards: {
          ...state.cards,
          [action.cardId]: {
            ...state.cards[action.cardId],
            lifecycleState: action.card.lifecycleState,
          },
        },
      }

    case types.CHANGE_PIN_SUCCESS:
      return {
        ...state,
        ...successState(action.type),
        cards: {
          ...state.cards,
          [action.cardId]: {
            ...state.cards[action.cardId],
            pinDetails: action.card.pinDetails,
          },
        },
      }

    case types.CHANGE_CVV_SUCCESS:
      return {
        ...state,
        ...successState(action.type),
      }

    case types.VIEW_PIN_SUCCESS:
      return {
        ...state,
        ...successState(action.type),
        cards: {
          ...state.cards,
          [action.cardId]: {
            ...state.cards[action.cardId],
            pinDetails: action.card.pinDetails,
            viewPinReenterCvv: true,
          },
        },
      }
    case types.DEACTIVATE_CARD_SUCCESS:
      return {
        ...state,
        cards: {
          ...state.cards,
          [action.cardId]: {
            ...state.cards[action.cardId],
            lifecycleState: action?.card?.lifecycleState,
          },
        },
      }
    case types.REACTIVATE_CARD_SUCCESS:
      const reactivatedCard = action?.card
      const updateToCard = {}

      if (reactivatedCard?.lifecycleState) {
        updateToCard.lifecycleState = reactivatedCard.lifecycleState
      }

      return {
        ...state,
        cards: {
          ...state.cards,
          [action.cardId]: {
            ...state.cards[action.cardId],
            ...updateToCard,
          },
        },
      }

    case types.CREATE_CARD_FAILURE:
    case types.FETCH_CARDS_FAILURE:
    case types.FETCH_CARD_FAILURE:
    case types.CANCEL_CARD_FAILURE:
    case types.CHANGE_PIN_FAILURE:
    case types.CHANGE_CVV_FAILURE:
    case types.VIEW_PIN_FAILURE:
      return {
        ...state,
        isFetching: false,
        noContent: false,
        fetchError: action.fetchError,
        fetchRetry: action.fetchRetry,
      }

    // the purpose of this is to remove ONLY blocking spinners/shimmers/errors/retries
    // TODO rename to indicate intent to something like "reset fetch-related loaders and errors"
    case types.RESET_CARD_REQUEST:
      return {
        ...state,
        isFetching: false,
        noContent: false,
        fetchSuccessAction: '',
        fetchError: null,
        fetchRetry: null,
      }

    // to remove user's class 1 data (PIN)
    case types.VIEW_PIN_ERASE:
      const cardsKeys = Object.keys(state.cards[action.cardId])
      const indexOfPinDetails = cardsKeys.findIndex((key) => key === 'pinDetails')
      if (indexOfPinDetails === -1) {
        return state
      }

      cardsKeys.splice(indexOfPinDetails, 1)

      return {
        ...state,
        cards: {
          ...state.cards,
          [action.cardId]: pick(state.cards[action.cardId], cardsKeys),
        },
      }

    default:
      return state
  }
}

export default card
